// One Transactions surface, mounted by both tills.
//
// The native POS gives it a bottom-tab pane; the legacy till opens it from
// the More menu. Everything the screen needs — the business day, which sale
// is being refunded, the modal — lives here rather than in either shell, so
// the two tills cannot drift apart in behaviour.
import React, { useCallback, useEffect, useState } from 'react';
import tenantStorage from '../../../services/tenantStorage';
import RefundModal from './RefundModal';
import TransactionsList from './TransactionsList';

// `tenantConfig` is threaded from the shell rather than read here because
// it is what decides whether the refund slip can be dialled directly
// (`printing.method === 'network'`) — the same value every other print
// dispatch in this codebase is handed by its caller. Both shells already
// hold it: the native station from useAuth, the legacy till from its own
// props.
const TransactionsPane = ({ tenantConfig, t }) => {
    const [salesDate, setSalesDate] = useState(null);
    const [refundSale, setRefundSale] = useState(null);
    // Bumped after a refund so the list refetches and the sale picks up its
    // Refunded badge without the cashier leaving the tab.
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        tenantStorage.getState('salesDate')
            .then((value) => { if (!cancelled) setSalesDate(value || null); })
            .catch(() => { if (!cancelled) setSalesDate(null); });
        return () => { cancelled = true; };
    }, []);

    const onRefunded = useCallback(() => {
        setRefundSale(null);
        setReloadKey((n) => n + 1);
        // A refund with returned items puts ingredients back. The sell
        // surface owns the stock figures on the product tiles and has no
        // idea this happened, so it showed the pre-refund count until the
        // page was reloaded. Fired unconditionally: refunds are rare, and a
        // missed refresh is visible to a cashier whereas a redundant one is
        // not.
        window.dispatchEvent(new CustomEvent('stockChanged'));
    }, []);

    // Only the current business day is refundable from a till. Older sales
    // are a portal job — not something to do mid-service.
    if (!salesDate) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                {t('no_transactions_today')}
            </div>
        );
    }

    return (
        <>
            <TransactionsList
                key={reloadKey}
                salesDate={salesDate}
                onSelect={setRefundSale}
                t={t}
            />
            <RefundModal
                open={Boolean(refundSale)}
                sale={refundSale}
                lines={refundSale?.items || []}
                onClose={() => setRefundSale(null)}
                onRefunded={onRefunded}
                tenantConfig={tenantConfig}
                t={t}
            />
        </>
    );
};

export default TransactionsPane;
